import { useCallback, useMemo } from 'react';
import { useSettings } from '@/entities/settings';
import { applyTopLevelOrder, mergeTopLevelOrder } from '../lib';
import type { NavigationNode } from './types';

/**
 * Результат {@link useTopLevelNodesOrder}.
 */
export type TopLevelNodesOrder = {
  /** Узлы, верхний уровень которых упорядочен по настройке пользователя. */
  orderedNodes: NavigationNode[];
  /** Сохраняет новый порядок верхнеуровневых узлов открытого диалога. */
  reorderTopLevelNodes: (nodeIds: string[]) => void;
};

/**
 * Порядок верхнеуровневых блоков атрибутов.
 *
 * Порядок — настройка пользователя: он хранится в настройках интерфейса и применяется
 * одинаково к меню навигации и к «портянке» значений, поэтому оба представления не
 * расходятся. Перестановка в одном диалоге не сдвигает блоки, которых в нём нет.
 */
export const useTopLevelNodesOrder = (nodes: NavigationNode[]): TopLevelNodesOrder => {
  const { dialogsPage, setDialogsSettings } = useSettings();
  const savedOrder = dialogsPage.attrTopLevelOrder;

  const orderedNodes = useMemo(() => applyTopLevelOrder(nodes, savedOrder), [nodes, savedOrder]);

  const reorderTopLevelNodes = useCallback(
    (nodeIds: string[]) => {
      setDialogsSettings({ attrTopLevelOrder: mergeTopLevelOrder(savedOrder, nodeIds) });
    },
    [savedOrder, setDialogsSettings],
  );

  return { orderedNodes, reorderTopLevelNodes };
};
